import React from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { IconCaretRightCircle } from '../../icons/library'

/**
 * A scroll area component that uses Radix ScrollArea under the hood.
 * It has a custom scrollbar and can show arrows when at the edges of the scroll area that jump you to the other side.
 *
 * @param props.className - Additional class names to add to the scroll area.
 * @param props.showArrows - Whether to show the arrows or not.
 * @param props.children - The content of the scroll area.
 */
const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & { showArrows?: boolean }
>(({ className, children, showArrows, ...props }, ref) => {
  const [hovering, setHovering] = React.useState(false)
  const [notAtRight, setNotAtRight] = React.useState(false)
  const [notAtLeft, setNotAtLeft] = React.useState(false)
  const viewportRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    const handleScroll = () => {
      if (viewportRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = viewportRef.current
        setNotAtRight(scrollLeft + clientWidth < scrollWidth - 1)
        setNotAtLeft(scrollLeft > 0)
      }
    }
    const viewport = viewportRef.current
    const resizeObserver = new ResizeObserver(handleScroll)
    if (viewport) {
      viewport.addEventListener('scroll', handleScroll)
      resizeObserver.observe(viewport)
      handleScroll()
    }
    return () => {
      if (viewport) {
        viewport.removeEventListener('scroll', handleScroll)
        resizeObserver.unobserve(viewport)
      }
    }
  }, [])

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={`relative overflow-hidden ${className || ''}`}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport ref={viewportRef} className="h-full w-full rounded-[inherit]">
        {children}
      </ScrollAreaPrimitive.Viewport>
      {showArrows && (
        <>
          <div
            onClick={() => {
              if (viewportRef.current) {
                viewportRef.current.scrollTo({
                  left: viewportRef.current.scrollLeft + viewportRef.current.clientWidth,
                  behavior: 'smooth',
                })
              }
            }}
            className={`absolute -right-1.5 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transform cursor-pointer rounded-full bg-white px-1 py-0.5 text-charcoal-active shadow transition-opacity ${hovering && notAtRight ? 'opacity-100' : 'pointer-events-none opacity-0'} duration-150`}
          >
            <IconCaretRightCircle size="lg" />
          </div>
          <div
            onClick={() => {
              if (viewportRef.current) {
                viewportRef.current.scrollTo({
                  left: 0,
                  behavior: 'smooth',
                })
              }
            }}
            className={`absolute left-5 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transform cursor-pointer rounded-full bg-white px-1 py-0.5 text-charcoal-active shadow transition-opacity ${hovering && notAtLeft ? 'opacity-100' : 'pointer-events-none opacity-0'} duration-150`}
          >
            <IconCaretRightCircle size="lg" className="rotate-180 transform" />
          </div>
        </>
      )}
    </ScrollAreaPrimitive.Root>
  )
})
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className = 'flex-1 rounded-full bg-gray-300/30', orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    forceMount
    className={`flex cursor-pointer touch-none select-none transition-opacity data-[state=hidden]:opacity-0 data-[state=visible]:opacity-100 ${
      orientation === 'vertical' ? 'h-full w-2.5 px-[2px]' : 'h-2.5 flex-col py-[2px]'
    }`}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className={`${className}`} />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
